import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineSpinner } from "@/components/common/Loading";
import { ShieldCheck, ShieldAlert, Plus, Globe, RefreshCw, DownloadCloud, Trash2, Pencil, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const TOPIC_LABEL = { regulatory: "Regulator", carrier: "Carrier", internal_process: "Internal Process" };
const TOPIC_CLS = {
  regulatory: "bg-indigo-50 text-indigo-700 border-indigo-200",
  carrier: "bg-sky-50 text-sky-700 border-sky-200",
  internal_process: "bg-teal-50 text-teal-700 border-teal-200",
};
const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  needs_review: "bg-amber-50 text-amber-700 border-amber-200",
};
const BLANK = {
  name: "", source_type: "regulator", authority_topic: "regulatory", domain: "", url: "",
  applicable_state: "ALL", purpose: "support", category: "", subcategory: "", program: "",
  content: "", allow_live_fetch: false, specific_procedure: false, status: "draft", scope: "account",
};

export default function CertifiedSources() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [docs, setDocs] = useState([]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const archiveDoc = async (d) => {
    if (!window.confirm(`Archive document "${d.title}" system-wide?`)) return;
    try { await api.delete(`/copilot/certified-documents/${d.id}`); toast.success("Document archived"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const docAction = async (d, path, okMsg) => {
    try { await api.post(`/copilot/certified-documents/${d.id}/${path}`, {}); toast.success(okMsg); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        api.get("/copilot/certified-sources/meta"),
        api.get("/copilot/certified-sources"),
      ]);
      setMeta(m.data);
      setItems(l.data.items || []);
      try { const dd = await api.get("/copilot/certified-documents"); setDocs(dd.data.items || []); } catch { /* read-only */ }
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const verify = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/copilot/certified-sources/${c.id}/verify`);
      if (data.verified) toast.success(`Verified (${data.automated_status})`);
      else toast.error(`${data.automated_status} — ${data.reach_error || "not verified"}`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };
  const adminConfirm = async (c) => {
    const notes = window.prompt(`Admin verification exception for "${c.name}".\nReason/evidence (official domain confirmed):`, "Official public domain independently confirmed by admin.");
    if (notes === null) return;
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/copilot/certified-sources/${c.id}/admin-confirm`, { notes, evidence: c.url || `https://${c.domain}` });
      toast.success(`Admin confirmed (${data.verification_method})`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };
  const fetchNow = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/copilot/certified-sources/${c.id}/fetch?force=true`);
      toast.success(`Fetched ${data.chars} chars${data.cached ? " (cached)" : ""}`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };
  const del = async (c) => {
    if (!window.confirm(`Archive "${c.name}"? It will no longer be used in generation.`)) return;
    try { await api.delete(`/copilot/certified-sources/${c.id}`); toast.success("Archived"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const verifySelected = async () => {
    try {
      const { data } = await api.post("/copilot/certified-sources/verify-selected", { ids: [...sel] });
      const pass = data.results.filter((r) => r.result === "passed").length;
      toast.success(`Verified ${pass}/${data.results.length} passed`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const publishSelected = async () => {
    try {
      const { data } = await api.post("/copilot/certified-sources/publish-selected", { ids: [...sel] });
      const pub = data.results.filter((r) => r.result === "published").length;
      const nv = data.results.filter((r) => r.result === "not_verified").length;
      toast.success(`Published ${pub}${nv ? ` · ${nv} not verified (skipped)` : ""}`);
      setSel(new Set()); await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="py-16 flex justify-center"><InlineSpinner /></div>;

  return (
    <div data-testid="certified-sources">
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500 max-w-2xl">
          Certified External Sources are authoritative regulator / carrier references merged into the Copilot's
          Trusted Knowledge Retrieval. Each must pass a reachability check (HTTP 200 + HTTPS/cert) and admin approval
          before it's used, and be <b>Published</b>. Conflicts with internal content are surfaced for review, never auto-resolved.
        </p>
        <Button className="bg-[#006090] hover:bg-[#004E75] shrink-0" onClick={() => setEditing({ ...BLANK })} data-testid="cs-add">
          <Plus className="w-4 h-4 mr-1.5" /> Add Certified Source
        </Button>
      </div>

      {sel.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 rounded-xl border border-[#006090]/30 bg-[#006090]/5 px-3 py-2" data-testid="cs-bulk-bar">
          <span className="text-sm font-semibold text-[#006090]">{sel.size} selected</span>
          <Button size="sm" variant="outline" onClick={verifySelected} data-testid="cs-verify-selected"><RefreshCw className="w-3.5 h-3.5 mr-1" />Verify Selected</Button>
          <Button size="sm" variant="outline" onClick={publishSelected} data-testid="cs-publish-selected"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Publish Selected Verified</Button>
          <Button size="sm" variant="ghost" onClick={() => setSel(new Set())} className="ml-auto">Clear</Button>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Globe} title="No certified sources yet"
          description="Register a regulator or carrier source, verify reachability, then publish to use it in the Copilot." />
      ) : (
        <div className="space-y-2" data-testid="cs-list">
          {items.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cs-row-${c.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} className="w-4 h-4" data-testid={`cs-select-${c.id}`} />
                <span className="font-semibold text-slate-800">{c.name}</span>
                {c.change_flag && <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`cs-changed-${c.id}`}>Source Content Changed</Badge>}
                {c.verified
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`cs-verified-${c.id}`}><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>
                  : <Badge className="bg-rose-50 text-rose-700 border-rose-200" data-testid={`cs-unverified-${c.id}`}><ShieldAlert className="w-3 h-3 mr-1" />Unverified</Badge>}
                <Badge variant="outline" className={TOPIC_CLS[c.authority_topic]}>{TOPIC_LABEL[c.authority_topic] || c.authority_topic}</Badge>
                {c.automated_status && <Badge variant="outline" className="text-[10px]" data-testid={`cs-autostatus-${c.id}`}>{c.automated_status}</Badge>}
                {String(c.verification_method || "").startsWith("ADMIN_CONFIRMED") && <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]">Admin Exception</Badge>}
                <Badge variant="outline" className={STATUS_CLS[c.status] || ""}>{c.status}</Badge>
                <Badge variant="outline">{(c.applicable_state || "ALL")}</Badge>
                {c.allow_live_fetch && <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">Live fetch</Badge>}
              </div>
              <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                <a href={c.url || `https://${c.domain}`} target="_blank" rel="noreferrer" className="text-[#006090] hover:underline">{c.domain}</a>
                {c.category && <span>· {c.category}{c.subcategory ? ` / ${c.subcategory}` : ""}</span>}
                {c.last_checked && <span className="inline-flex items-center gap-1">· {c.reachable ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-rose-500" />}HTTP {c.http_status ?? "—"}</span>}
                {c.cache?.fetched_at && <span>· cached {String(c.cache.fetched_at).slice(0, 10)}</span>}
              </div>
              {c.reach_error && !c.verified && <p className="text-xs text-rose-600 mt-1">{c.reach_error}</p>}
              {c.content && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{c.content}</p>}
              {c.can_manage && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" disabled={busyId === c.id} onClick={() => verify(c)} data-testid={`cs-verify-${c.id}`}>
                    {busyId === c.id ? <InlineSpinner /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Verify reachability</>}
                  </Button>
                  <Button size="sm" variant="outline" disabled={busyId === c.id} onClick={() => adminConfirm(c)} data-testid={`cs-admin-confirm-${c.id}`}><ShieldCheck className="w-3.5 h-3.5 mr-1" />Admin Confirm</Button>
                  {c.allow_live_fetch && (
                    <Button size="sm" variant="outline" disabled={busyId === c.id || !c.verified} onClick={() => fetchNow(c)} data-testid={`cs-fetch-${c.id}`}>
                      <DownloadCloud className="w-3.5 h-3.5 mr-1" />Fetch now
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`cs-edit-${c.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                  <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => del(c)} data-testid={`cs-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Archive</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {docs.length > 0 && (
        <div className="mt-6" data-testid="cs-documents">
          <h3 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1.5"><Globe className="w-4 h-4" />Certified Documents ({docs.length})</h3>
          <p className="text-[11px] text-slate-400 mb-3 max-w-3xl" data-testid="cs-doc-faq">
            <b>Scope:</b> <b>System Documents</b> are shared across every district (only a Super Admin can publish or delete them). <b>Group Added Documents</b> belong to a single district. A Group Admin who proposes a System document creates it as <b>Pending</b> until a Super Admin approves it; Group Admins can archive a System document for their own group and restore it later.
          </p>
          {[["system", "System Documents"], ["group", "Group Added Documents"]].map(([sc, label]) => {
            const list = docs.filter((d) => (d.scope || "system") === sc);
            if (!list.length) return null;
            return (
              <div key={sc} className="mb-4" data-testid={`cs-docs-${sc}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{label} ({list.length})</p>
                <div className="rounded-xl border border-slate-200 bg-white divide-y">
                  {list.map((d) => (
                    <div key={d.id} className={`flex items-center gap-3 px-4 py-2.5 ${d.group_archived ? "opacity-60" : ""}`} data-testid={`cs-doc-${d.id}`}>
                      <div className="min-w-0 flex-1">
                        <a href={d.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-[#006090] hover:underline truncate block">{d.title}</a>
                        <p className="text-[11px] text-slate-400 flex flex-wrap gap-x-2">
                          <span>{d.document_type}</span>{d.applicable_state && <span>· {d.applicable_state}</span>}
                          {d.topic && <span>· {d.topic}</span>}{d.authority && <span>· {d.authority}</span>}
                        </p>
                      </div>
                      {d.pending_approval && <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`cs-doc-pending-${d.id}`}>Pending Approval</Badge>}
                      {d.group_archived && <Badge variant="outline" className="bg-slate-100 text-slate-500">Archived for your group</Badge>}
                      <Badge variant="outline" className={d.status === "current" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}>{d.status}</Badge>
                      {d.can_approve && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => docAction(d, "approve", "Approved system-wide")} data-testid={`cs-doc-approve-${d.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>}
                      {d.can_group_archive && <Button size="sm" variant="outline" onClick={() => docAction(d, "group-archive", "Archived for your group")} data-testid={`cs-doc-grouparchive-${d.id}`}>Archive for my group</Button>}
                      {d.can_group_restore && <Button size="sm" variant="outline" onClick={() => docAction(d, "group-restore", "Restored for your group")} data-testid={`cs-doc-restore-${d.id}`}>Restore</Button>}
                      {d.can_delete && <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => archiveDoc(d)} data-testid={`cs-doc-archive-${d.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <CsDialog meta={meta} value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CsDialog({ meta, value, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK, ...value });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isNew = !value.id;

  const save = async () => {
    if (!form.name?.trim() || !(form.domain?.trim() || form.url?.trim())) {
      toast.error("Name and a domain or URL are required"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.organization_id) delete payload.organization_id;
      if (isNew) await api.post("/copilot/certified-sources", payload);
      else await api.patch(`/copilot/certified-sources/${value.id}`, payload);
      toast.success(isNew ? "Certified source added" : "Saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="cs-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add Certified External Source" : "Edit Certified Source"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cs-f-name" placeholder="e.g. California Department of Insurance" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">URL</Label>
              <Input value={form.url} onChange={(e) => set("url", e.target.value)} data-testid="cs-f-url" placeholder="https://…" /></div>
            <div><Label className="text-xs">Domain</Label>
              <Input value={form.domain} onChange={(e) => set("domain", e.target.value)} data-testid="cs-f-domain" placeholder="insurance.ca.gov" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Scope</Label>
              <Select value={form.scope} onValueChange={(v) => set("scope", v)}>
                <SelectTrigger className="h-9" data-testid="cs-f-scope"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="account">Account</SelectItem>
                  <SelectItem value="group">Group (District)</SelectItem>
                  {meta?.can_system && <SelectItem value="system">System</SelectItem>}
                </SelectContent></Select></div>
            {form.scope === "group" ? (
              <div><Label className="text-xs">District</Label>
                <Select value={form.organization_id || "none"} onValueChange={(v) => set("organization_id", v === "none" ? "" : v)}>
                  <SelectTrigger className="h-9" data-testid="cs-f-org"><SelectValue placeholder="Select district" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{(meta?.organizations || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
            ) : <div />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Source type</Label>
              <Select value={form.source_type} onValueChange={(v) => set("source_type", v)}>
                <SelectTrigger className="h-9" data-testid="cs-f-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{(meta?.source_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Authority topic</Label>
              <Select value={form.authority_topic} onValueChange={(v) => set("authority_topic", v)}>
                <SelectTrigger className="h-9" data-testid="cs-f-authority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{(meta?.authority_topics || []).map((t) => <SelectItem key={t} value={t}>{TOPIC_LABEL[t] || t}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">State</Label>
              <Input value={form.applicable_state} onChange={(e) => set("applicable_state", e.target.value.toUpperCase().slice(0, 3))} data-testid="cs-f-state" placeholder="CA / ALL" /></div>
            <div><Label className="text-xs">Purpose</Label>
              <Select value={form.purpose} onValueChange={(v) => set("purpose", v)}>
                <SelectTrigger className="h-9" data-testid="cs-f-purpose"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{(meta?.purposes || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-9" data-testid="cs-f-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Category</Label>
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="cs-f-category" placeholder="Licensing" /></div>
            <div><Label className="text-xs">Subcategory</Label>
              <Input value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} data-testid="cs-f-subcategory" placeholder="P&C" /></div>
          </div>
          <div><Label className="text-xs">Curated authoritative snippet</Label>
            <Textarea rows={4} value={form.content} onChange={(e) => set("content", e.target.value)} data-testid="cs-f-content"
              placeholder="Paste the authoritative text from this source (used directly in generation)." /></div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div><p className="text-sm font-medium text-slate-700">Allow live on-demand fetch</p>
              <p className="text-xs text-slate-500">Fetch current page text from the verified domain (24h cache).</p></div>
            <Switch checked={!!form.allow_live_fetch} onCheckedChange={(v) => set("allow_live_fetch", v)} data-testid="cs-f-livefetch" /></div>
          {form.authority_topic === "carrier" && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><p className="text-sm font-medium text-slate-700">Internal is a more specific procedure</p>
                <p className="text-xs text-slate-500">If set, matching internal content wins carrier conflicts.</p></div>
              <Switch checked={!!form.specific_procedure} onCheckedChange={(v) => set("specific_procedure", v)} data-testid="cs-f-specific" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={saving} onClick={save} data-testid="cs-save">{saving ? <InlineSpinner /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
